"use client";

import { useState } from "react";

const VISIT_STAGES = [
  { key: "opening", label: "Odprtje" },
  { key: "uncovering_needs", label: "Potrebe" },
  { key: "solution", label: "Rešitev" },
  { key: "objection_handling", label: "Ugovori" },
  { key: "closing", label: "Zaključek" },
] as const;

interface SessionOnboardingProps {
  profileName: string;
  profileRole: string;
  productName?: string;
  maxSessionMinutes: number;
  onStart: () => void;
  onDismiss: () => void;
}

export function onboardingStorageKey(agentId: string) {
  return `farmavoice_onboarding_complete_${agentId}`;
}

export function SessionOnboarding({
  profileName,
  profileRole,
  productName,
  maxSessionMinutes,
  onStart,
  onDismiss,
}: SessionOnboardingProps) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: "Scenarij obiska",
      body: (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            Simulirate kratki terenski obisk pri zdravstvenem delavcu (HCP). AI
            igra vlogo:
          </p>
          <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-4">
            <p className="font-semibold text-white">{profileName}</p>
            <p className="text-sm text-slate-400 mt-0.5">{profileRole}</p>
            {productName && (
              <p className="text-sm text-blue-300 mt-3">
                Produkt: <span className="text-blue-200">{productName}</span>
              </p>
            )}
          </div>
          <p className="text-sm text-slate-400">
            Obisk traja približno{" "}
            <span className="text-slate-200 font-medium">
              {maxSessionMinutes} min
            </span>
            . Govorite naravno — kot v resnični ordinaciji.
          </p>
        </div>
      ),
    },
    {
      title: "Cilj: 5-stopenjski obisk",
      body: (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            Strukturirajte pogovor po farmacevtskem modelu obiska. Po seji
            prejmete oceno za vsako fazo.
          </p>
          <ol className="flex flex-col gap-2">
            {VISIT_STAGES.map((stage, i) => (
              <li
                key={stage.key}
                className="flex items-center gap-3 rounded-lg bg-slate-800/40 border border-slate-700/60 px-3 py-2"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600/80 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-200">{stage.label}</span>
              </li>
            ))}
          </ol>
        </div>
      ),
    },
    {
      title: "Priprava",
      body: (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            Pred začetkom preverite naslednje:
          </p>
          <ul className="flex flex-col gap-3">
            {[
              "Dovolite dostop do mikrofona, ko vas brskalnik vpraša.",
              "Izberite tišje okolje — hrup vpliva na kakovost prepoznave govora.",
              "Govorite jasno in v normalnem tempu; AI vas sliši v realnem času.",
              "Po koncu obiska prejmete mentorjevo analizo in predlog naslednje vaje.",
            ].map((tip) => (
              <li
                key={tip}
                className="flex gap-2.5 text-sm text-slate-300 leading-snug"
              >
                <span className="text-emerald-400 shrink-0 mt-0.5">✓</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? "w-6 bg-blue-500"
                    : i < step
                      ? "w-3 bg-blue-700"
                      : "w-3 bg-slate-700"
                }`}
                aria-hidden="true"
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1"
          >
            Preskoči
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          <h2
            id="onboarding-title"
            className="text-lg font-semibold text-white"
          >
            {current.title}
          </h2>
          {current.body}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-800 bg-slate-900/80">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Nazaj
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={onStart}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Začni trening
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Naprej
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
